import React, { useEffect } from 'react';
import { Controller, UseControllerProps } from 'react-hook-form';
import {
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  SxProps,
  Theme,
  Typography,
  Box,
} from '@mui/material';

interface ControlledRadioFieldProps {
  label?: string;
  id: string;
  name: string;
  className?: string;
  disabled?: boolean;
  control: any;
  defaultValue?: any;
  rules: UseControllerProps['rules'];
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
  setValue?: any;
  options: { value: string | number; label: string }[]; // Improved type for options
  align?: 'Horizontal' | 'Vertical';
  sx?: SxProps<Theme>;
  multilineValue?: boolean
}

const WSRadioField: React.FC<ControlledRadioFieldProps> = ({
  id,
  label,
  className,
  control,
  name,
  rules,
  disabled = false,
  defaultValue = '',
  setValue,
  onChange,
  options,
  sx,
  align = 'Horizontal', // Default alignment to Horizontal
  multilineValue = false,
}) => {
  useEffect(() => {
    if (setValue) {
      setValue(name, defaultValue);
    }
  }, [setValue, name, defaultValue]);

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={rules}
      render={({ field, fieldState }) => (
        <FormControl
          className={className}
          disabled={disabled}
          error={!!fieldState.error}
          component="fieldset"
          fullWidth // This makes FormControl take up the full available width
        >
          {label && (
            <FormLabel sx={{ fontSize: '0.875rem', marginTop: -1 }}>{label}</FormLabel>
          )}
          <RadioGroup
            row={align === 'Horizontal'} // Use row when alignment is Horizontal
            sx={{ fontSize: '0.875rem', ...sx }}
            aria-labelledby={id}
            {...field}
            onChange={(event) => {
              field.onChange(event);
              if (onChange) {
                onChange(event);
              }
            }}
          >
            {options.map((option) => (
              <>
                {multilineValue ? (
                  <Box sx={{ width: '100%', marginTop: 2,border:'1px solid rgba(0, 0, 0, 0.12)' ,padding:4,borderRadius:2 }}>
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={
                        <Radio
                          sx={{
                            '& .MuiSvgIcon-root': {
                              fontSize: '0.875rem',
                            },
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                          {option.label}
                        </Typography>
                      }
                      disabled={disabled}
                      sx={{ width: '100%' }}
                    />
                  </Box>
                ) : (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={
                      <Radio
                        sx={{
                          '& .MuiSvgIcon-root': {
                            fontSize: '0.875rem',
                          },
                        }}
                      />
                    }
                    label={<span style={{ fontSize: '0.875rem' }}>{option.label}</span>}
                    disabled={disabled}
                    sx={{ width: '100%' }}
                  />
                )}
              </>
            ))}
          </RadioGroup>
          {fieldState.error && (
            <FormHelperText sx={{ fontSize: '0.875rem' }}>
              {fieldState.error.message}
            </FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
};

export default WSRadioField;
